import { useState } from "react";
import { Link } from "react-router-dom";
import { ActionModal, FIELD_LABEL_CLASS, INPUT_CLASS } from "./ActionModal";
import {
  HOTEL_STATE_DISPLAY,
  LOW_VOLUME_EXPLANATION,
  LOW_VOLUME_LOCK_DISCLOSURE,
  PAUSED_MOVED_TO_CRM_BODY,
  PAUSED_MOVED_TO_CRM_LINK,
  PAUSED_MOVED_TO_CRM_TITLE,
} from "../../vocabulary";
import { useCrmEnabled } from "../../../../../helpers/hooks/use-crm-enabled";
import type { OverrideState } from "../../types";

/** Render order, and deliberately the contract's: Low volume last, the same
 *  way the tiles and the fleet table order it.
 *
 *  **Paused is not here** (ADR 0041). It stays a readable state everywhere —
 *  the gate still derives it, and the hotels holding a standing override keep
 *  theirs — but it can no longer be PLACED, and the backend 400s on it. The
 *  option is removed rather than shown disabled: a disabled option in a native
 *  select is a dead end an operator has to hover to understand, and what they
 *  actually need is the route that replaced it, which the block below gives
 *  them unprompted. */
const OVERRIDABLE: OverrideState[] = [
  "healthy",
  "monitoring",
  "unhealthy",
  "low-volume",
];

/** Set a status override (§5.2): forces only the REPORTED status, among the
 *  three health states and Low volume — Not-yet-live and Paused are not
 *  placeable, and a gate outranks whatever is chosen here.
 *
 *  Since ADR 0040 no override carries lock machinery: Low volume used to apply
 *  both locks on placement and now applies none, because ADR 0039's allowance
 *  stands the booking rules down without the hotel going dark. The disclosure
 *  it shows on selection says that, rather than warning about a consequence
 *  that no longer happens. */
export function OverrideModal({
  onSubmit,
  onClose,
  pending,
}: {
  onSubmit: (body: {
    state: OverrideState;
    reason: string;
    note: string;
  }) => void;
  onClose: () => void;
  pending?: boolean;
}) {
  const [state, setState] = useState<OverrideState>("healthy");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const lowVolume = state === "low-volume";
  const crmEnabled = useCrmEnabled();

  return (
    <ActionModal
      title="Set status override"
      submitLabel={lowVolume ? "Place in Low volume" : "Set override"}
      onSubmit={() =>
        onSubmit({ state, reason: reason.trim(), note: note.trim() })
      }
      onClose={onClose}
      pending={pending}
      disabled={!reason.trim() || !note.trim()}
    >
      <div>
        <label className={FIELD_LABEL_CLASS}>Reported state</label>
        <select
          className={INPUT_CLASS}
          value={state}
          onChange={(e) => setState(e.target.value as OverrideState)}
        >
          {OVERRIDABLE.map((s) => (
            <option key={s} value={s}>
              {HOTEL_STATE_DISPLAY[s]}
            </option>
          ))}
        </select>
      </div>
      {lowVolume && (
        <div className="rounded bg-warning-light/50 px-3 py-2.5 space-y-1.5">
          <p className="text-xs text-content-primary">
            {LOW_VOLUME_EXPLANATION}
          </p>
          <p className="text-xs text-warning-dark font-medium">
            {LOW_VOLUME_LOCK_DISCLOSURE}
          </p>
        </div>
      )}
      {/* Neutral, never amber: an operator looking for Paused has not done
          anything wrong, they are in the wrong console. The link goes to the
          customer LIST, not to a customer page — the Test control is a per-row
          cell on the grid and the detail page has none, so a deep link would
          land somewhere the switch does not exist. */}
      <div className="rounded bg-gray-50 border border-[#E9E9EB] px-3 py-2.5 space-y-1.5">
        <p className="text-xs font-medium text-content-primary">
          {PAUSED_MOVED_TO_CRM_TITLE}
        </p>
        <p className="text-xs text-gray-600">{PAUSED_MOVED_TO_CRM_BODY}</p>
        {crmEnabled && (
          <Link
            to="/crm/customers"
            className="inline-block text-xs font-medium text-blue-600 hover:underline"
          >
            {PAUSED_MOVED_TO_CRM_LINK} →
          </Link>
        )}
      </div>
      <div>
        <label className={FIELD_LABEL_CLASS}>Reason (required)</label>
        <textarea
          className={`${INPUT_CLASS} min-h-[66px]`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
      <div>
        <label className={FIELD_LABEL_CLASS}>Note (required)</label>
        <textarea
          className={`${INPUT_CLASS} min-h-[66px]`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      <p className="text-xs text-gray-400">
        The computed state stays displayed beside the override and computation
        continues untouched. A gate still wins: a Not-yet-live or Paused hotel
        reports its gate and the override waits underneath. A break the override
        holds invisible stops pushing to Slack, but keeps its worklist row and
        its 7-day reminder.
      </p>
    </ActionModal>
  );
}
